import React, { useState, useRef, forwardRef, useImperativeHandle, useCallback, useEffect, useMemo, useContext, ReactNode } from "react";
import {
  ChatMessageAttachmentType,
  ChatMessagePayloadConference,
} from "@jugl-web/rest-api";
import { logger } from "@web-src/utils/logger";
import cx from "classnames";
import {
  useIsDraggingFiles,
  usePrevious,
  useTranslations,
} from "@jugl-web/utils";
import {
  CHAT_TEXT_AREA_NAMESPACE,
  ChatTextarea,
  MentionTextareaHandle,
  MentionTextareaProps,
  getLexicalConfig,
} from "@jugl-web/domain-resources/common/components/RichTextarea";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useFCM } from "@web-src/modules/notifications/providers/FCMProvider/useFCM";
import { useOnboarding } from "@web-src/modules/preferences/providers/useOnboarding";
import { DroppableFileInput, PlainButton } from "@jugl-web/ui-components";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import { compressImage } from "@jugl-web/domain-resources/files/utils/compressImage";
import { environment } from "@web-src/environments/environment";
import { CallsContext } from "@web-src/modules/conference/pages/ConferencePage/providers/CallsContext";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { MENTIONS_REGEXP } from "@jugl-web/utils/utils/mentions";
import { decodeHTMLEntities } from "@jugl-web/utils/utils/decodeHTMLEntities";
import {
  ChatInputAudioRecorder,
  ChatInputAudioRecorderHandle,
} from "./components/ChatInputAudioRecorder";
import { ReactComponent as MicrophoneIcon } from "./assets/microphone.svg";
import {
  ChatInputToolbar,
  ChatInputToolbarParams,
  ToolbarActions,
} from "./components/ChatInputToolbar";
import useChatInputState, { ChatInputState } from "./hooks/useChatInputState";
import { ChatAddConferenceSidebar } from "./components/ChatAddConferenceSidebar";
import { getAttachmentTypeByMimeType } from "../../utils";
import {
  ChatInputActionInfo,
  ChatInputActionInfoProps,
} from "./components/ChatInputActionInfo";
import { VideoMeetingAttachment } from "../VideoMeetingAttachment";
import { ChatInputFilePreview } from "./components/ChatInputFilePreview";
import { ChatInputButton } from "./components/ChatInputButton";
import { AttachmentType } from "../../types";
import { useChatSelectFiles } from "./hooks/useChatSelectFiles";
import { getAttachmentType } from "../../utils/getAttachmentType";
import { ChatInputTextFormattingBar } from "./components/ChatInputTextFormattingBar/ChatInputTextFormattingBar";

const MAX_FILE_SIZE = 75;
const FOCUS_INPUT_DELAY_MS = 300;

export type ChatInputHandle = {
  clear: () => void;
  setValue: (
    body: string,
    options?: { mentionsInfo: { id: string; name: string }[] }
  ) => void;
  focus: () => void;
};

export type ChatInputParams = {
  id: string;
  isLoading?: boolean;
  onSubmit: (params: {
    body: string;
    attachments?: {
      file: File;
      type: ChatMessageAttachmentType;
      caption?: string;
    }[];
    gifDirectUrl?: string;
    conference?: ChatMessagePayloadConference;
  }) => void;
  toolbarActions?: ToolbarActions;
  actionMessageInfo?: ChatInputActionInfoProps;
  mentions?: MentionTextareaProps["mentions"];
  allowSubmitEmpty?: boolean;
  maxTextLenght?: number;
};
export const ChatInput = forwardRef<ChatInputHandle, ChatInputParams>(
  (
    {
      isLoading = false,
      onSubmit,
      toolbarActions = {},
      id,
      mentions,
      actionMessageInfo,
      allowSubmitEmpty,
      maxTextLenght,
    },
    ref
  ) => {
    const { t } = useTranslations();
    const attachmentType = useRef<AttachmentType>(undefined);
    const isSubmitting = useRef(false);
    const isDragging = useIsDraggingFiles(!toolbarActions.attachments?.enabled);
    const { activeCallProps } = useContext(CallsContext);

    const config = useMemo(
      () => getLexicalConfig(CHAT_TEXT_AREA_NAMESPACE, false, false),
      []
    );

    const maxAttachments = toolbarActions.attachments?.isMultiple
      ? environment.featureFlags.maxChatAttachmentsCount
      : 1;

    const {
      chatsState: permanentChatsState,
      chatsTempState,
      updateChatState,
      isInitialized: isChatsStateInitialized,
    } = useChatInputState();

    const chatsState =
      actionMessageInfo?.type === "edit" ? chatsTempState : permanentChatsState;

    const handleUpdateChatState = useCallback(
      (state: Exclude<ChatInputState, "bodyRaw">, reset?: boolean) => {
        updateChatState(id, state, {
          temp: actionMessageInfo?.type === "edit",
          merge: !reset,
        });
      },
      [updateChatState, id, actionMessageInfo?.type]
    );

    const getFormattedMessageBody = useCallback(() => {
      let markdown = $bodyInput.current?.saveToMarkdown() || "";
      if (!environment.escapeChatChars) {
        markdown = markdown.replace(/\\([\\*_~%`])/g, "$1");
      }
      return decodeHTMLEntities(markdown);
    }, []);

    const handleOnFormatUpdate = useCallback(() => {
      handleUpdateChatState({
        body: $bodyInput.current?.saveToMarkdown()
          ? $bodyInput.current?.saveToJSON()
          : undefined,
        bodyRaw: getFormattedMessageBody(),
      });
    }, [getFormattedMessageBody, handleUpdateChatState]);

    const attachments = useMemo(
      () => chatsState?.[id]?.attachments || [],
      [chatsState, id]
    );

    const conference = useMemo(
      () => chatsState?.[id]?.conference,
      [chatsState, id]
    );

    const { getCorrectUploadedFiles } = useChatSelectFiles({
      filesCount: attachments.length,
      maxAttachments,
      acceptType: toolbarActions.attachments?.accept,
    });

    const [isAddConfernceSidebarOpen, setIsAddConfernceSidebarOpen] =
      useState<boolean>(false);
    const [isTextFormattingBarOpen, setIsTextFormattingBarOpen] =
      useState(false);

    const [isValid, setIsValid] = useState(false);
    const [recordingAudio, setRecordingAudio] = useState<boolean>(false);

    useEffect(() => {
      setRecordingAudio(false);
      audioRecorder$?.current?.reset();
    }, [id]);
    useEffect(() => {
      setRecordingAudio((prev) => {
        if (chatsState[id]?.voiceRecordingFile) {
          return true;
        }
        return prev;
      });
    }, [chatsState, id]);

    useEffect(() => {
      if (recordingAudio && activeCallProps?.channel) {
        audioRecorder$.current?.stop();
      }
    }, [activeCallProps?.channel, recordingAudio]);

    const $bodyInput = useRef<MentionTextareaHandle | null>(null);
    const previousId = usePrevious(id);
    const { logEvent } = useFCM();
    const { entityId } = useEntitySelectedProvider();
    const { imageCompressionPreferences } = useUserEntityPreferences({
      entityId,
    });
    const { selectFile } = useFileSelect();

    const canAddMoreAttachments =
      (chatsState[id]?.attachments?.length || 0) < maxAttachments;

    const previousActionMessageInfo = usePrevious(actionMessageInfo);
    const previousIsChatsStateInitialized = usePrevious(
      isChatsStateInitialized
    );
    useEffect(() => {
      let shouldSkip =
        (previousIsChatsStateInitialized && previousId === id) ||
        !isChatsStateInitialized;
      if (
        previousActionMessageInfo?.type === "edit" &&
        (!actionMessageInfo ||
          actionMessageInfo.type !== previousActionMessageInfo?.type ||
          actionMessageInfo.text !== previousActionMessageInfo?.text)
      ) {
        shouldSkip = false;
      }
      if (shouldSkip) {
        return;
      }
      const chatsStateBody = chatsState?.[id]?.body;
      if (chatsStateBody) {
        $bodyInput.current?.readFromJSON(chatsStateBody);
      } else {
        $bodyInput.current?.setRichText("");
      }
    }, [
      previousId,
      id,
      chatsState,
      previousActionMessageInfo?.type,
      previousActionMessageInfo,
      actionMessageInfo,
      isChatsStateInitialized,
      previousIsChatsStateInitialized,
    ]);

    useEffect(() => {
      if (previousId !== id) {
        setIsTextFormattingBarOpen(false);
        $bodyInput.current?.focus();
      }
      attachmentType.current = chatsState[id]?.attachmentType;
    }, [chatsState, id, previousId]);

    const validator = useCallback(
      (text?: string) => {
        if (allowSubmitEmpty) {
          return true;
        }
        return (
          !!text?.trim().length ||
          !!chatsState[id]?.attachments?.length ||
          !!conference
        );
      },
      [chatsState, id, conference, allowSubmitEmpty]
    );

    useEffect(() => {
      setIsValid(validator($bodyInput.current?.getRichText()));
    }, [validator, chatsState]);

    const handleRawTextChange = (rawText: string) => {
      handleUpdateChatState({
        body: $bodyInput.current?.saveToMarkdown()
          ? $bodyInput.current?.saveToJSON()
          : undefined,
        bodyRaw: getFormattedMessageBody(),
      });
      setIsValid(validator(rawText));
    };

    const { completeOnboardingStep, isOnboardingActive } = useOnboarding();

    const handleSubmit = useCallback(() => {
      if (
        isSubmitting.current ||
        (!allowSubmitEmpty &&
          !getFormattedMessageBody() &&
          !attachments.length &&
          !conference)
      )
        return;
      isSubmitting.current = true;

      onSubmit({
        attachments: attachments.length
          ? attachments.map(({ file, caption }) => ({
              file,
              type: getAttachmentTypeByMimeType(file.type),
              caption,
            }))
          : undefined,
        body: getFormattedMessageBody(),
        conference,
      });
      if (isOnboardingActive) {
        completeOnboardingStep("chat");
      }
      setTimeout(() => {
        isSubmitting.current = false;
        $bodyInput.current?.focus();
      }, FOCUS_INPUT_DELAY_MS);
      logEvent("action_chat_send");
    }, [
      allowSubmitEmpty,
      getFormattedMessageBody,
      attachments,
      conference,
      onSubmit,
      isOnboardingActive,
      logEvent,
      completeOnboardingStep,
    ]);

    const handleEnter = useCallback(() => {
      if (isValid && !isLoading) {
        handleSubmit();
      }
    }, [handleSubmit, isValid, isLoading]);

    const handleFileRemoveClick = useCallback(
      (index: number) => {
        handleUpdateChatState({
          attachments: attachments.filter((_, idx) => idx !== index),
        });
      },
      [attachments, handleUpdateChatState]
    );

    const audioRecorder$ = useRef<ChatInputAudioRecorderHandle>(null);
    const handleAudioRecorderCancel = useCallback(() => {
      handleUpdateChatState({
        voiceRecordingFile: undefined,
      });
      setRecordingAudio(false);
    }, [handleUpdateChatState]);

    const handleAudioRecorderSend = useCallback(
      async (e: File) => {
        try {
          await onSubmit({
            body: "",
            attachments: [
              {
                file: e,
                type: ChatMessageAttachmentType.voice,
              },
            ],
          });
          setRecordingAudio(false);
          setTimeout(() => {
            $bodyInput.current?.focus();
          }, FOCUS_INPUT_DELAY_MS);
        } catch (error) {
          logger.error(error);
        }
      },
      [onSubmit]
    );

    useImperativeHandle(ref, () => ({
      clear: () => {
        $bodyInput.current?.setRichText("");
        handleUpdateChatState(
          {
            attachments: [],
            attachmentType: undefined,
            conference: undefined,
          },
          true
        );
      },
      setValue: async (newBody, options) => {
        let bodyToSet = newBody;
        if (options?.mentionsInfo) {
          const messageMentions: { id: string; name: string }[] =
            options.mentionsInfo;
          messageMentions.forEach(({ id: mentionId, name }) => {
            if (bodyToSet.includes(`@${mentionId}`)) {
              bodyToSet = bodyToSet.replaceAll(
                new RegExp(`@${mentionId}`, "g"),
                `@${mentionId}%%#${name.replace("@", "")}#%%`
              );
            }
          });
        }
        const bodyWithMentions = bodyToSet.replace(
          MENTIONS_REGEXP,
          (_, mentionId, name) =>
            `@<${mentionId}>[${name.replace(/ /g, "__SPACE__")}]`
        );
        const bodyWithSpacesHandled = bodyWithMentions.replace(
          /(?<!^ {0,3}(?:[*+-]|\d{1,9}[.)])) /gm,
          "&#32;"
        );
        const bodyWithSpacesRestoredInMentions = bodyWithSpacesHandled.replace(
          /__SPACE__/g,
          " "
        );
        $bodyInput.current?.readFromMarkdown(bodyWithSpacesRestoredInMentions);
      },
      focus: () => {
        $bodyInput.current?.focus();
      },
    }));

    const processUploadedFiles = useCallback(
      (uploadedFiles?: File[], restrictType?: boolean) => {
        if (!uploadedFiles?.length) return;

        if (!attachmentType.current || (!attachments.length && !restrictType)) {
          const types = uploadedFiles
            .map((file) => getAttachmentType(file.type))
            .filter((type) => type !== null);

          if (!types.length) return;

          const detectedType = types[0];

          if (detectedType !== null) {
            attachmentType.current = detectedType;
          }
        }

        const currentAttachmentType = attachmentType.current;
        const maxAllowedSize =
          attachmentType.current && toolbarActions.attachments?.maxSize
            ? toolbarActions.attachments.maxSize[attachmentType.current]
            : MAX_FILE_SIZE;

        if (currentAttachmentType) {
          handleUpdateChatState({
            attachments: [
              ...attachments,
              ...getCorrectUploadedFiles(
                uploadedFiles,
                currentAttachmentType,
                maxAllowedSize
              ).map((file) => ({
                file,
                caption: "",
              })),
            ],
            attachmentType: currentAttachmentType,
          });
        }
      },
      [
        attachments,
        toolbarActions.attachments,
        handleUpdateChatState,
        getCorrectUploadedFiles,
      ]
    );

    const handleDropFiles = useCallback(
      async (droppedFiles?: File[]) => {
        const compressedFiles = await Promise.all(
          droppedFiles?.map(
            async (file) =>
              new Promise<File>((resolve, reject) => {
                const isImage = file.type.includes("image");
                if (!isImage) {
                  resolve(file);
                  return;
                }
                compressImage(file, imageCompressionPreferences?.chats?.quality)
                  .then(resolve)
                  .catch(reject);
              })
          ) || []
        );
        processUploadedFiles(compressedFiles);
      },
      [imageCompressionPreferences?.chats?.quality, processUploadedFiles]
    );

    const handleFileInputPaste = useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (toolbarActions.attachments?.enabled) {
          processUploadedFiles(Array.from(e.clipboardData.files));
        }
      },
      [processUploadedFiles, toolbarActions.attachments?.enabled]
    );

    const handleSelectFile = useCallback(async () => {
      const selectedFiles = await selectFile({
        acceptTypes: attachmentType.current
          ? toolbarActions.attachments?.accept?.[attachmentType.current]
          : "*",
        compressionConfig: {
          image: imageCompressionPreferences?.chats?.quality,
        },
        multiple: toolbarActions.attachments?.isMultiple,
      });
      if (selectedFiles && selectedFiles.length > 0) {
        processUploadedFiles(selectedFiles, true);
      }
    }, [
      selectFile,
      toolbarActions.attachments?.accept,
      imageCompressionPreferences?.chats?.quality,
      processUploadedFiles,
      toolbarActions.attachments?.isMultiple,
    ]);

    const handleChatInputToolbarClick: ChatInputToolbarParams["onAction"] =
      useCallback(
        ({ type, emoji, gif, attachmentType: selectedAttachmentType }) => {
          switch (type) {
            case "attachments":
              if (!selectedAttachmentType) break;
              attachmentType.current = selectedAttachmentType;
              handleSelectFile();
              break;
            case "emoji":
              if (emoji) {
                $bodyInput?.current?.focus();
                $bodyInput.current?.insertTextAtSelection(emoji);
              }
              break;
            case "gif":
              onSubmit?.({
                gifDirectUrl: gif,
                body: "",
              });
              break;
            case "videoMessage":
              setIsAddConfernceSidebarOpen(true);
              break;
            case "textFormatting":
              setIsTextFormattingBarOpen((prev) => !prev);
              break;
            default:
          }
        },
        [handleSelectFile, onSubmit]
      );

    const handleFileEdit = useCallback(
      ({
        index,
        file,
        caption,
      }: {
        index: number;
        file: File;
        caption: string;
      }) => {
        handleUpdateChatState({
          attachments: attachments.map((att, idx) =>
            idx === index ? { ...att, file, caption } : att
          ),
        });
      },
      [attachments, handleUpdateChatState]
    );

    const handleAddAttachment = useCallback(() => {
      handleChatInputToolbarClick({
        type: "attachments",
        attachmentType: attachmentType.current,
      });
    }, [handleChatInputToolbarClick, attachmentType]);

    return (
      <LexicalComposer initialConfig={config}>
        <>
          {isDragging && (
            <DroppableFileInput
              onUpdate={handleDropFiles}
              accept={
                toolbarActions.attachments?.accept &&
                attachmentType.current &&
                !!attachments.length
                  ? toolbarActions.attachments.accept[attachmentType.current]
                  : "*"
              }
              multiple={toolbarActions.attachments?.isMultiple}
              customContent={
                <div className="flex flex-col gap-1">
                  {t(
                    {
                      id: "chats-page.drop-files-here-to-send-as-message-attachment",
                      defaultMessage:
                        "<title>Drop files here to send</title><subtitle>as message attachment</subtitle>",
                    },
                    {
                      title: (text: ReactNode) => (
                        <span className="font-dark font-secondary text-lg leading-[27px] tracking-[0.18px]">
                          {text}
                        </span>
                      ),
                      subtitle: (text: ReactNode) => (
                        <span className="font-grey-900 font-secondary leading-4 tracking-[0.16px]">
                          {text}
                        </span>
                      ),
                    }
                  )}
                </div>
              }
            />
          )}
          <div className={cx({ hidden: isDragging })}>
            {toolbarActions.textFormatting?.enabled && (
              <ChatInputTextFormattingBar
                onUpdate={handleOnFormatUpdate}
                isOpen={isTextFormattingBarOpen}
              />
            )}
            {actionMessageInfo && (
              <ChatInputActionInfo {...actionMessageInfo} />
            )}
            <ChatAddConferenceSidebar
              isOpen={isAddConfernceSidebarOpen}
              onClose={() => setIsAddConfernceSidebarOpen(false)}
              setConference={(conferenceToSet) =>
                handleUpdateChatState({ conference: conferenceToSet })
              }
            />
            <ChatInputFilePreview
              onEdit={handleFileEdit}
              canAddMoreAttachments={canAddMoreAttachments}
              attachments={attachments}
              isLoading={isLoading}
              onRemoveClick={handleFileRemoveClick}
              onAddAttachment={handleAddAttachment}
              mentions={mentions}
              hideCaptionInput={toolbarActions.attachments?.isCaptionHidden}
            />
            {conference && (
              <VideoMeetingAttachment
                conference={conference}
                isPreview
                onCancel={() =>
                  handleUpdateChatState({ conference: undefined })
                }
              />
            )}
            <div className="flex items-center rounded-[16px] bg-gray-50 px-6 py-4">
              {recordingAudio && (
                <div className="flex-1">
                  <ChatInputAudioRecorder
                    ref={audioRecorder$}
                    defaultRecordingFile={chatsState[id]?.voiceRecordingFile}
                    onSend={handleAudioRecorderSend}
                    onCancel={handleAudioRecorderCancel}
                    onRecordingAvailable={(file: File) => {
                      handleUpdateChatState({
                        voiceRecordingFile: file,
                      });
                    }}
                    isLoading={isLoading}
                  />
                </div>
              )}
              <div
                className={cx("flex-1", recordingAudio ? "hidden" : undefined)}
              >
                <ChatTextarea
                  externalLexicalComposer
                  maxTextLength={
                    typeof maxTextLenght === "number" ? maxTextLenght : 10000
                  }
                  ref={$bodyInput}
                  mentions={mentions}
                  placeholder={t({
                    id: "chats-page.type-new-message",
                    defaultMessage: "Type a new message",
                  })}
                  onRawTextChange={handleRawTextChange}
                  onPaste={handleFileInputPaste}
                  onEnter={handleEnter}
                  classes={{
                    placeholder: "text-grey-900",
                    contentEditable:
                      "outline-none max-h-[200px] overflow-y-auto",
                  }}
                />
              </div>
              {!recordingAudio && (
                <div className="flex items-center">
                  {toolbarActions?.audioMessage?.enabled && (
                    <PlainButton
                      disabled={
                        !!$bodyInput.current?.saveToMarkdown() ||
                        !!attachments.length ||
                        !!conference ||
                        !!activeCallProps?.channel
                      }
                      className="mr-4 h-11 w-11 rounded-full transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-transparent"
                      onClick={() => {
                        setRecordingAudio(true);
                        logEvent("action_chat_voice");
                      }}
                    >
                      <MicrophoneIcon />
                    </PlainButton>
                  )}
                  <ChatInputButton
                    isLoading={isLoading}
                    isDisabled={!isValid || isLoading}
                    onClick={handleSubmit}
                  />
                </div>
              )}
            </div>
            <div className="mt-2 pl-[16px]">
              <ChatInputToolbar
                id={id}
                onAction={handleChatInputToolbarClick}
                disabledNewFiles={!!recordingAudio || !!conference}
                nonEditableText={!!recordingAudio}
                onAddAttachments={
                  !!attachments.length && attachmentType.current
                    ? () =>
                        handleChatInputToolbarClick({
                          type: "attachments",
                          attachmentType: attachmentType.current,
                        })
                    : undefined
                }
                toolbarActions={toolbarActions}
              />
            </div>
          </div>
        </>
      </LexicalComposer>
    );
  }
);
